import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// Модерна, минималистична раница/куфар с градиенти
const PremiumBackpack = ({ isClosed, isHovered }) => (
    <motion.div
        animate={{
            scale: isHovered && !isClosed ? 1.05 : 1,
            rotate: isClosed ? [0, -5, 5, 0] : 0
        }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        style={{
            position: 'relative',
            width: '140px',
            height: '140px',
            filter: 'drop-shadow(0px 20px 30px rgba(0,0,0,0.15))'
        }}
    >
        <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="bagGrad" x1="60" y1="20" x2="60" y2="110" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#3F3F46" />
                    <stop offset="1" stopColor="#18181B" />
                </linearGradient>
                <linearGradient id="pocketGrad" x1="60" y1="60" x2="60" y2="100" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#52525B" />
                    <stop offset="1" stopColor="#27272A" />
                </linearGradient>
                <linearGradient id="accent" x1="0" y1="0" x2="100" y2="100">
                    <stop stopColor="#FBBF24" />
                    <stop offset="1" stopColor="#F59E0B" />
                </linearGradient>
            </defs>

            {/* Дръжка */}
            <path d="M45 30V20C45 14.4772 49.4772 10 55 10H65C70.5228 10 75 14.4772 75 20V30" stroke="#A1A1AA" strokeWidth="6" strokeLinecap="round" />

            {/* Основно тяло */}
            <rect x="25" y="25" width="70" height="85" rx="20" fill="url(#bagGrad)" />

            {/* Преден джоб */}
            <rect x="35" y="55" width="50" height="40" rx="12" fill="url(#pocketGrad)" />

            {/* Ципове и детайли */}
            <path d="M25 45C45 50 75 50 95 45" stroke="#27272A" strokeWidth="4" />

            {/* Индикатор за статус (отворено/затворено) */}
            <circle cx="60" cy="75" r="6" fill={isClosed ? "#10B981" : "url(#accent)"} />
            {isClosed && <path d="M57 75L59 77L63 73" stroke="#18181B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
        </svg>
    </motion.div>
);

const INITIAL_ITEMS = [
    { id: 'rose', emoji: '🌹', correct: true, labelBg: 'Роза', labelEn: 'Rose' },
    { id: 'matryoshka', emoji: '🪆', correct: false, labelBg: 'Матрьошка', labelEn: 'Matryoshka', errorBg: 'Опа! Това е руско, а не българско!', errorEn: "Oops! That's Russian, not Bulgarian!" },
    { id: 'sombrero', emoji: '👒', correct: false, labelBg: 'Сомбреро', labelEn: 'Sombrero', errorBg: 'Ай карамба! Това е от Мексико!', errorEn: "¡Ay, caramba! That's from Mexico!" },
    { id: 'cyrillic', isCustom: true, correct: true, labelBg: 'Кирилица', labelEn: 'Cyrillic' },
    { id: 'yogurt', emoji: '🥣', correct: true, labelBg: 'Кис. мляко', labelEn: 'Yogurt' },
    { id: 'croissant', emoji: '🥐', correct: false, labelBg: 'Кроасан', labelEn: 'Croissant', errorBg: 'Мон дьо! Това е от Франция!', errorEn: "Mon Dieu! That belongs in France!" }
];
const PackYourBagHook = ({ onStartGame }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language === 'bg' ? 'bg' : 'en';

    const [items, setItems] = useState(INITIAL_ITEMS);
    const [packedIds, setPackedIds] = useState([]);
    const [errorMsg, setErrorMsg] = useState('');
    const [isHoveringDrop, setIsHoveringDrop] = useState(false);

    const dropZoneRef = useRef(null);
    const isFinished = packedIds.length === 3;

    const texts = {
        bg: {
            title: 'Сложи багажа за София',
            instruction: 'Провлачи 3-те автентични български предмета в раницата си.',
            packed: 'събрани',
            startBtn: 'ЗАПОЧНИ ПРИКЛЮЧЕНИЕТО',
            winTitle: 'Готов си за пътя!',
            winDesc: 'Раницата ти е перфектно подредена. Време е да разгадаеш тайните на София.',
        },
        en: {
            title: 'Pack for Sofia',
            instruction: 'Drag the 3 authentic local items into your backpack.',
            packed: 'packed',
            startBtn: 'START EXPLORING',
            winTitle: 'You are ready!',
            winDesc: 'Your backpack is perfectly packed. Time to uncover the secrets of Sofia.',
        }
    };
    const t = texts[currentLang];

    // Логика при пускане на предмет
    const handleDragEnd = (event, info, item) => {
        setIsHoveringDrop(false);

        if (!dropZoneRef.current) return;
        const dropZone = dropZoneRef.current.getBoundingClientRect();
        const pointer = { x: info.point.x, y: info.point.y };

        // Проверка дали курсорът е върху зоната на раницата
        const isInsideDropZone =
            pointer.x >= dropZone.left &&
            pointer.x <= dropZone.right &&
            pointer.y >= dropZone.top &&
            pointer.y <= dropZone.bottom;

        if (isInsideDropZone) {
            if (item.correct) {
                // Успешно добавяне
                if (navigator.vibrate) navigator.vibrate(50); // Лека вибрация за реализъм
                setPackedIds(prev => [...prev, item.id]);
            } else {
                // Грешен предмет
                if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
                setErrorMsg(currentLang === 'bg' ? item.errorBg : item.errorEn);
                setTimeout(() => setErrorMsg(''), 2500);
            }
        }
    };

    return (
        <div style={{
            position: 'relative',
            width: '100%',
            height: '100vh',
            maxWidth: '500px',
            margin: '0 auto',
            overflow: 'hidden',
            // Премиум тъмен градиент за повече контраст и дълбочина
            background: 'radial-gradient(circle at 50% 0%, #2A2A35 0%, #111116 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            color: '#FFFFFF',
            fontFamily: '"Inter", "Outfit", sans-serif',
        }}>
            {/* Тост за грешка с Glassmorphism */}
            <AnimatePresence>
                {errorMsg && (
                    <motion.div
                        initial={{ opacity: 0, y: -50 }}
                        animate={{ opacity: 1, y: 20 }}
                        exit={{ opacity: 0, y: -50 }}
                        style={{
                            position: 'absolute',
                            top: '20px',
                            zIndex: 5000,
                            background: 'rgba(239, 68, 68, 0.85)',
                            backdropFilter: 'blur(10px)',
                            padding: '12px 24px',
                            borderRadius: '100px',
                            fontWeight: '600',
                            fontSize: '14px',
                            boxShadow: '0 10px 25px rgba(239, 68, 68, 0.4)',
                            border: '1px solid rgba(255,255,255,0.2)'
                        }}
                    >
                        {errorMsg}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Заглавна част */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                style={{ textAlign: 'center', padding: '40px 20px 20px', zIndex: 10 }}
            >
                <h1 style={{ fontSize: '32px', fontWeight: '800', margin: '0 0 10px 0', background: 'linear-gradient(to right, #FCD34D, #F59E0B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    {t.title}
                </h1>
                <p style={{ fontSize: '15px', color: '#A1A1AA', lineHeight: '1.5', margin: 0 }}>
                    {t.instruction}
                </p>
            </motion.div>

            {/* Зона с предмети */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '15px',
                padding: '20px',
                width: '100%',
                flex: 1,
                alignContent: 'flex-start',
                zIndex: 20
            }}>
                <AnimatePresence>
                    {!isFinished && items.map((item) => {
                        const isPacked = packedIds.includes(item.id);
                        if (isPacked) return null; // Скриваме го, щом е в раницата

                        return (
                            <motion.div
                                key={item.id}
                                drag
                                dragConstraints={{ top: -100, left: -100, right: 100, bottom: 400 }}
                                dragSnapToOrigin={true} // Винаги се връща, ако не е пуснат в раницата
                                onDragStart={() => setIsHoveringDrop(true)}
                                onDragEnd={(e, info) => handleDragEnd(e, info, item)}
                                whileDrag={{ scale: 1.15, zIndex: 999, rotate: 5 }}
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                                transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                style={{
                                    width: '80px',
                                    height: '100px',
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    borderRadius: '20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'grab',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                                }}
                            >

                                {item.isCustom && item.id === 'cyrillic' ? (
                                    <div style={{
                                        width: '46px',
                                        height: '46px',
                                        background: 'linear-gradient(135deg, #FBBF24 0%, #D97706 100%)',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#111116',
                                        fontWeight: '900',
                                        fontSize: '18px',
                                        fontFamily: '"Times New Roman", serif',
                                        letterSpacing: '1px',
                                        boxShadow: '0 5px 15px rgba(217, 119, 6, 0.4), inset 0 2px 5px rgba(255, 255, 255, 0.4)',
                                        textShadow: '0 1px 1px rgba(255,255,255,0.3)'
                                    }}>
                                        АБВ
                                    </div>
                                ) : (
                                    <span style={{ fontSize: '42px', filter: 'drop-shadow(0px 5px 5px rgba(0,0,0,0.3))' }}>
                                        {item.emoji}
                                    </span>
                                )}

                                <span style={{ fontSize: '12px', fontWeight: '600', marginTop: '10px', color: '#E4E4E7' }}>
                                    {currentLang === 'bg' ? item.labelBg : item.labelEn}
                                </span>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>

            {/* Зона за пускане (Раницата) */}
            <motion.div
                ref={dropZoneRef}
                animate={{
                    borderColor: isHoveringDrop ? 'rgba(251, 191, 36, 0.8)' : 'rgba(255,255,255,0.1)',
                    backgroundColor: isHoveringDrop ? 'rgba(251, 191, 36, 0.05)' : 'transparent',
                }}
                style={{
                    position: 'relative',         // ПРЕМАХНАТО е абсолютното позициониране
                    width: '90%',
                    height: '180px',              // Леко намалена височина за малки екрани
                    flexShrink: 0,                // Гарантира, че раницата няма да се смачка
                    marginBottom: '30px',         // Дистанция от дъното на екрана
                    border: '2px dashed',
                    borderRadius: '30px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    transition: 'all 0.3s ease'
                }}
            >
                <PremiumBackpack isClosed={isFinished} isHovered={isHoveringDrop} />

                {/* Брояч */}
                {!isFinished && (
                    <motion.div
                        key={packedIds.length}
                        initial={{ scale: 1.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        style={{
                            marginTop: '15px',
                            background: 'rgba(255,255,255,0.1)',
                            padding: '6px 16px',
                            borderRadius: '20px',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#FCD34D'
                        }}
                    >
                        {packedIds.length} / 3 {t.packed}
                    </motion.div>
                )}
            </motion.div>

            {/* Финален екран (Победа) */}
            <AnimatePresence>
                {isFinished && (
                    <motion.div
                        initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
                        animate={{ opacity: 1, backdropFilter: 'blur(20px)' }}
                        style={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            background: 'rgba(17, 17, 22, 0.85)',
                            zIndex: 2000,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '30px',
                            textAlign: 'center'
                        }}
                    >
                        <motion.div
                            initial={{ scale: 0, rotate: -10 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ type: "spring", bounce: 0.5 }}
                        >
                            <PremiumBackpack isClosed={true} isHovered={false} />
                        </motion.div>

                        <motion.h2
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                            style={{ fontSize: '28px', color: '#fff', margin: '20px 0 10px 0' }}
                        >
                            {t.winTitle}
                        </motion.h2>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                            style={{ color: '#A1A1AA', fontSize: '15px', lineHeight: '1.6', marginBottom: '30px' }}
                        >
                            {t.winDesc}
                        </motion.p>

                        <motion.button
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={onStartGame}
                            style={{
                                background: 'linear-gradient(135deg, #FBBF24 0%, #D97706 100%)',
                                color: '#000',
                                border: 'none',
                                padding: '16px 36px',
                                borderRadius: '100px',
                                fontSize: '16px',
                                fontWeight: '800',
                                letterSpacing: '0.5px',
                                cursor: 'pointer',
                                boxShadow: '0 10px 25px rgba(245, 158, 11, 0.4)'
                            }}
                        >
                            {t.startBtn}
                        </motion.button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default PackYourBagHook;